import { platform } from '@/lib/platform'

// Base del backend NestJS (mismo patrón que src/lib/kyc/providers/demo.ts).
const API_BASE = (platform.domain?.adminApiUrl ?? '').replace(/\/$/, '')

export interface SecurityQuestionView {
  position: number
  question: string
}

export interface SecurityAnswerInput {
  position: number
  question: string
  answer: string
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`account-security api ${res.status}`)
  return res.json() as Promise<T>
}

// ── Config de preguntas (usuario con sesión) ─────────────────────────────────

/** GET /me/security-questions → { configured, questions[] } (sin respuestas). */
export function getSecurityQuestions(): Promise<{ configured: boolean; questions: SecurityQuestionView[] }> {
  return fetch(`${API_BASE}/me/security-questions`, { credentials: 'include' }).then((r) =>
    json<{ configured: boolean; questions: SecurityQuestionView[] }>(r),
  )
}

/** PUT /me/security-questions → guarda las 3 preguntas + respuestas (se hashean en el server). */
export function setSecurityQuestions(items: SecurityAnswerInput[]): Promise<{ ok: boolean; configured: boolean }> {
  return fetch(`${API_BASE}/me/security-questions`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  }).then((r) => json<{ ok: boolean; configured: boolean }>(r))
}

// ── Flujo de desbloqueo (usuario bloqueado, sin sesión) ──────────────────────

/** POST /unblock/start → { requestId, level, questions[] } (genérico si no aplica). */
export function startUnblock(email: string): Promise<{ ok: boolean; requestId: string | null; level?: number; questions: SecurityQuestionView[] }> {
  return fetch(`${API_BASE}/unblock/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  }).then((r) => json<{ ok: boolean; requestId: string | null; level?: number; questions: SecurityQuestionView[] }>(r))
}

/**
 * POST /unblock/:id/answer → resultado de la apelación:
 *   status='APPROVED' + unlocked=true → grado 1, la cuenta se reactivó sola.
 *   status='PENDING'                  → grado 2, queda a la espera del admin.
 *   status='LOCKED' + escalated=true  → grado 1 agotó intentos y escaló a grado 2.
 *   status='LOCKED'                   → grado 2 agotó intentos (solo admin).
 *   status='VERIFYING'                → respuesta incorrecta; quedan attemptsLeft.
 */
export interface AnswerUnblockResult {
  status: string
  attemptsLeft: number
  level?: number
  unlocked?: boolean
  escalated?: boolean
}
export function answerUnblock(
  requestId: string,
  answers: { position: number; answer: string }[],
): Promise<AnswerUnblockResult> {
  return fetch(`${API_BASE}/unblock/${encodeURIComponent(requestId)}/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  }).then((r) => json<AnswerUnblockResult>(r))
}

// ── Recuperación de cuenta por pérdida de correo (usuario sin sesión) ─────────

/**
 * POST /recovery/request → solicita recuperar la cuenta indicando el correo NUEVO.
 * Respuesta genérica (no revela si la cuenta existe). Crea el ticket en BigAdmin.
 */
export function requestRecovery(email: string, newEmail: string): Promise<{ ok: boolean }> {
  return fetch(`${API_BASE}/recovery/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, newEmail }),
  }).then((r) => json<{ ok: boolean }>(r))
}

/** POST /recovery/:id/kyc-complete → tras el re-KYC, deja el ticket pendiente de aprobación. */
export function completeRecoveryKyc(requestId: string): Promise<{ status: string; diditStatus?: string }> {
  return fetch(`${API_BASE}/recovery/${encodeURIComponent(requestId)}/kyc-complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  }).then((r) => json<{ status: string; diditStatus?: string }>(r))
}
